import React, { useState } from "react";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";

import img1 from "../assets/images/Illustrations/finance.svg";
import img2 from "../assets/images/Illustrations/certification.svg";
import img3 from "../assets/images/Illustrations/transfer-money.svg";
import img4 from "../assets/images/Illustrations/performance-overview.svg";
import img5 from "../assets/images/Illustrations/visualization.svg";
import img6 from "../assets/images/Illustrations/workspace.svg";

import ServiceDialog from "./Dialogs/ServiceDialog";
import { BrowserView, MobileView } from "react-device-detect";

const carouselResponsiveConfig = {
    superLargeDesktop: {
        // the naming can be any, depends on you.
        breakpoint: { max: 4000, min: 3000 },
        items: 6,
    },
    desktop: {
        breakpoint: { max: 3000, min: 1024 },
        items: 4,
    },
    tablet: {
        breakpoint: { max: 1024, min: 464 },
        items: 2,
    },
    mobile: {
        breakpoint: { max: 464, min: 0 },
        items: 1,
    },
};

const serviceList = [
    {
        title: "Accounting & Bookkeeping",
        img: img1,
        description:
            "Stay on top of your finances with accurate, efficient bookkeeping and monthly accounting support tailored to your business.",
        items: [
            "Monthly management packs",
            "Actual vs budgeted comparisons",
            "Detailed cash flow planning",
            "Budget preparation and implementation",
            "Maintenance of books using computerized accounting systems",
            "Monthly bank and creditors reconciliations",
        ],
    },
    {
        title: "Tax & Statutory Compliance",
        img: img2,
        description:
            "From tax planning to SARS submissions, we ensure you're fully compliant — and never caught off guard.",
        items: [
            "Individual and provisional tax returns (IRP6 & IT12)",
            "Personal and business income tax planning",
            "Corporate tax returns (IT14)",
            "VAT registration and returns",
            "PAYE tax registration and returns",
            "EMP501 reconciliations",
            "Tax clearance certificates",
            "Tax directives",
            "Registration for income and provisional tax",
            "SARS queries, objections, and appeals",
            "Tax advisory for individuals and companies",
        ],
    },
    {
        title: "Payroll Administration",
        img: img3,
        description:
            "Reliable, on-time payroll solutions — including PAYE, UIF, and SDL — so your team is always taken care of.",
        items: [
            "Payroll processing and monthly employee payslips",
            "PAYE, UIF, and SDL calculations and submissions",
            "IRP5 preparation",
            "Tax compliance for expatriate employees",
            "Liaising with SARS and Department of Labour",
            "Employee data maintenance and compliance",
        ],
    },
    {
        title: "Financial Statements Compilation",
        img: img4,
        description:
            "Clear, compliant, and professional financial statements prepared in line with applicable reporting frameworks.",
        items: [
            "Preparation of annual financial statements",
            "Compliance reporting under relevant frameworks",
            "Supporting documentation and reconciliations",
        ],
    },
    {
        title: "Business Management & Advisory",
        img: img5,
        description:
            "Make confident decisions with expert insights. We offer strategic guidance to help your business grow and adapt.",
        items: [
            "Strategic business consulting",
            "Restructuring and company valuations",
            "Management consulting",
            "Human resource advisory",
            "Advice on accounting systems and software selection",
            "Computerization of accounting departments",
        ],
    },
    {
        title: "Secretarial Services",
        img: img6,
        description:
            "Let us handle your company registrations, CIPC filings, amendments, and ongoing compliance — seamlessly.",
        items: [
            "Company registrations and formations",
            "Annual returns and statutory filings",
            "Shareholder registry maintenance",
            "Corporate changes and CIPC submissions",
            "Issuing of compliance and statutory certificates",
            "Ongoing secretarial support",
        ],
    },
];

const Services = () => {
    const [selectedService, setSelectedService] = useState(null);

    const showDialog = () => selectedService != null;

    return (
        <div id="services">
            <BrowserView>
                <div className="bg-gray-100 py-12">
                    <section data-aos="zoom-in-down">
                        <div className="my-4 py-4">
                            <h2 className="my-2 text-center text-3xl fg-primary uppercase font-bold">services</h2>

                            <div className="flex justify-center">
                                <div className="w-24 border-b-4 border-primary"></div>
                            </div>
                            <h2 className="mt-4 mx-12 text-center text-xl lg:text-2xl font-semibold fg-primary">
                                We are deeply committed to the growth and success of our clients.
                            </h2>
                        </div>

                        <div data-aos="fade-down" data-aos-delay="600">
                            <Carousel
                                swipeable={false}
                                draggable={false}
                                responsive={carouselResponsiveConfig}
                                className="py-12"
                            >
                                {serviceList.map((service, i) => {
                                    const imageWidth = 250;
                                    const imageHeight = 300;
                                    const { title, img, description, items } = service;
                                    return (
                                        <div key={i}>
                                            <div
                                                className={`bg-white transition-all ease-in-out duration-400  overflow-hidden text-gray-700 hover:bg-gray-500 hover:text-white rounded-lg shadow-2xl p-3 clickable group ${
                                                    i < serviceList.length - 1 ? "mr-10" : ""
                                                }`}
                                                onClick={() => setSelectedService(service)}
                                            >
                                                <div className="m-2 text-justify text-sm">
                                                    <div className="service-img">
                                                        <img
                                                            alt="card img"
                                                            className="rounded-t group-hover:scale-[1.15] transition duration-1000 ease-in-out"
                                                            width={imageWidth}
                                                            height={imageHeight}
                                                            src={img}
                                                            draggable={false}
                                                        />
                                                    </div>
                                                    <h2 className="font-semibold my-4 text-2xl text-center">{title}</h2>
                                                    <p className="text-md font-medium">{description}</p>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </Carousel>
                        </div>
                    </section>

                    <section>
                        <ServiceDialog
                            showDialog={showDialog()}
                            title={selectedService?.title ?? ""}
                            description={selectedService?.description ?? ""}
                            items={selectedService?.items ?? []}
                            img={selectedService?.img}
                            onClose={() => setSelectedService(null)}
                            arrows={!showDialog()}
                        />
                    </section>

                    <section>
                        <div className="m-auto max-w-6xl p-2 md:p-12 h-5/6">
                            <div
                                className="flex flex-col-reverse lg:flex-row py-8 justify-between lg:text-left"
                                data-aos="zoom-out"
                            >
                                <div className="lg:w-1/2 flex flex-col lg:mx-4 justify-center">
                                    <div className="fg-primary mb-4">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="72"
                                            height="72"
                                            viewBox="0 0 24 24"
                                            className="fill-current"
                                        >
                                            <path d="M2 12h2a7.986 7.986 0 0 1 2.337-5.663 7.91 7.91 0 0 1 2.542-1.71 8.12 8.12 0 0 1 6.13-.041A2.488 2.488 0 0 0 17.5 7C18.886 7 20 5.886 20 4.5S18.886 2 17.5 2c-.689 0-1.312.276-1.763.725-2.431-.973-5.223-.958-7.635.059a9.928 9.928 0 0 0-3.18 2.139 9.92 9.92 0 0 0-2.14 3.179A10.005 10.005 0 0 0 2 12zm17.373 3.122c-.401.952-.977 1.808-1.71 2.541s-1.589 1.309-2.542 1.71a8.12 8.12 0 0 1-6.13.041A2.488 2.488 0 0 0 6.5 17C5.114 17 4 18.114 4 19.5S5.114 22 6.5 22c.689 0 1.312-.276 1.763-.725A9.965 9.965 0 0 0 12 22a9.983 9.983 0 0 0 9.217-6.102A9.992 9.992 0 0 0 22 12h-2a7.993 7.993 0 0 1-.627 3.122z"></path>
                                            <path d="M12 7.462c-2.502 0-4.538 2.036-4.538 4.538S9.498 16.538 12 16.538s4.538-2.036 4.538-4.538S14.502 7.462 12 7.462zm0 7.076c-1.399 0-2.538-1.139-2.538-2.538S10.601 9.462 12 9.462s2.538 1.139 2.538 2.538-1.139 2.538-2.538 2.538z"></path>
                                        </svg>
                                    </div>
                                    <h3
                                        className="text-3xl  fg-primary 
                            font-bold"
                                    >
                                        We <span className="font-black">Advise</span>
                                    </h3>
                                    <div>
                                        <p className="my-3 text-xl text-gray-600 font-semibold">
                                            With a foundation built on years of accounting and advisory experience, we
                                            offer practical, tailored solutions to help you make confident financial
                                            decisions — whether you're growing a business, managing risk, or planning
                                            for the future.
                                        </p>
                                    </div>
                                </div>
                                <div className="lg:w-1/2 flex flex-col lg:mx-4 justify-center">
                                    <div className="fg-primary mb-4">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="72"
                                            height="72"
                                            viewBox="0 0 24 24"
                                            className="fill-current"
                                        >
                                            <path d="m7.375 16.781 1.25-1.562L4.601 12l4.024-3.219-1.25-1.562-5 4a1 1 0 0 0 0 1.562l5 4zm9.25-9.562-1.25 1.562L19.399 12l-4.024 3.219 1.25 1.562 5-4a1 1 0 0 0 0-1.562l-5-4zm-1.649-4.003-4 18-1.953-.434 4-18z"></path>
                                        </svg>
                                    </div>
                                    <h3 className="text-3xl  fg-primary font-bold">
                                        We <span className="font-black">Partner</span>
                                    </h3>
                                    <div>
                                        <p className="my-3 text-xl text-gray-600 font-semibold">
                                            At Vero, we don’t just work for you — we work with you. We align our
                                            expertise with your business goals, providing ongoing support that adapts as
                                            your needs evolve.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </BrowserView>

            <MobileView>
                <div className="bg-gray-100 py-12">
                    <section>
                        <div className="my-4 py-4">
                            <h2 className="my-2 text-center text-3xl fg-primary uppercase font-bold">services</h2>

                            <div className="flex justify-center">
                                <div className="w-24 border-b-4 border-primary"></div>
                            </div>
                            <h2 className="mt-4 mx-12 text-center text-xl lg:text-2xl font-semibold fg-primary">
                                We are deeply committed to the growth and success of our clients.
                            </h2>
                        </div>

                        <Carousel
                            swipeable={true}
                            draggable={true}
                            responsive={carouselResponsiveConfig}
                            arrows={!showDialog()}
                        >
                            {serviceList.map((service, i) => {
                                const imageWidth = 250;
                                const imageHeight = 300;
                                const { title, img, description, items } = service;
                                return (
                                    <div key={i}>
                                        <div
                                            className="bg-white transition-all ease-in-out duration-400  overflow-hidden text-gray-700 hover:bg-gray-500 hover:text-white rounded-lg shadow-2xl p-3 clickable group mx-5"
                                            onClick={() => setSelectedService(service)}
                                        >
                                            <div className="m-2 text-justify text-sm">
                                                <div className="service-img">
                                                    <img
                                                        alt="card img"
                                                        className="rounded-t group-hover:scale-[1.15] transition duration-1000 ease-in-out"
                                                        width={imageWidth}
                                                        height={imageHeight}
                                                        src={img}
                                                        draggable={false}
                                                    />
                                                </div>
                                                <h2 className="font-semibold my-4 text-2xl text-center">{title}</h2>
                                                <p className="text-md font-medium">{description}</p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </Carousel>
                    </section>

                    <section>
                        <ServiceDialog
                            showDialog={showDialog()}
                            title={selectedService?.title ?? ""}
                            description={selectedService?.description ?? ""}
                            items={selectedService?.items ?? []}
                            img={selectedService?.img}
                            onClose={() => setSelectedService(null)}
                        />
                    </section>

                    <section>
                        <div className="px-12">
                            <div>
                                <div className="flex flex-col justify-center mt-8">
                                    <div className="fg-primary mb-4">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="72"
                                            height="72"
                                            viewBox="0 0 24 24"
                                            className="fill-current"
                                        >
                                            <path d="M2 12h2a7.986 7.986 0 0 1 2.337-5.663 7.91 7.91 0 0 1 2.542-1.71 8.12 8.12 0 0 1 6.13-.041A2.488 2.488 0 0 0 17.5 7C18.886 7 20 5.886 20 4.5S18.886 2 17.5 2c-.689 0-1.312.276-1.763.725-2.431-.973-5.223-.958-7.635.059a9.928 9.928 0 0 0-3.18 2.139 9.92 9.92 0 0 0-2.14 3.179A10.005 10.005 0 0 0 2 12zm17.373 3.122c-.401.952-.977 1.808-1.71 2.541s-1.589 1.309-2.542 1.71a8.12 8.12 0 0 1-6.13.041A2.488 2.488 0 0 0 6.5 17C5.114 17 4 18.114 4 19.5S5.114 22 6.5 22c.689 0 1.312-.276 1.763-.725A9.965 9.965 0 0 0 12 22a9.983 9.983 0 0 0 9.217-6.102A9.992 9.992 0 0 0 22 12h-2a7.993 7.993 0 0 1-.627 3.122z"></path>
                                            <path d="M12 7.462c-2.502 0-4.538 2.036-4.538 4.538S9.498 16.538 12 16.538s4.538-2.036 4.538-4.538S14.502 7.462 12 7.462zm0 7.076c-1.399 0-2.538-1.139-2.538-2.538S10.601 9.462 12 9.462s2.538 1.139 2.538 2.538-1.139 2.538-2.538 2.538z"></path>
                                        </svg>
                                    </div>
                                    <h3
                                        className="text-3xl  fg-primary 
                            font-bold"
                                    >
                                        We <span className="font-black">Advise</span>
                                    </h3>
                                    <div>
                                        <p className="my-3 text-xl text-gray-600 font-semibold">
                                            With a foundation built on years of accounting and advisory experience, we
                                            offer practical, tailored solutions to help you make confident financial
                                            decisions — whether you're growing a business, managing risk, or planning
                                            for the future.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex flex-col justify-center mt-8">
                                    <div className="fg-primary mb-4">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="72"
                                            height="72"
                                            viewBox="0 0 24 24"
                                            className="fill-current"
                                        >
                                            <path d="m7.375 16.781 1.25-1.562L4.601 12l4.024-3.219-1.25-1.562-5 4a1 1 0 0 0 0 1.562l5 4zm9.25-9.562-1.25 1.562L19.399 12l-4.024 3.219 1.25 1.562 5-4a1 1 0 0 0 0-1.562l-5-4zm-1.649-4.003-4 18-1.953-.434 4-18z"></path>
                                        </svg>
                                    </div>
                                    <h3 className="text-3xl  fg-primary font-bold">
                                        We <span className="font-black">Partner</span>
                                    </h3>
                                    <div>
                                        <p className="my-3 text-xl text-gray-600 font-semibold">
                                            At Vero, we don't just work for you — we work with you. We align our
                                            expertise with your business goals, providing ongoing support that adapts as
                                            your needs evolve.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </MobileView>
        </div>
    );
};

export default Services;
